<script setup>
// Pole godziny v2 zamiast natywnego <input type="time"> — ten na komputerze to
// klikanie po segmentach (czasem z AM/PM) i nie da się wpisać „930". Tu godzinę
// się pisze jak się chce: „9", „930", „9.30", „21:15", „9 30" → „09:30"; po
// kliknięciu rozwija się lista co 15 minut wokół bieżącej godziny z „Teraz" na
// górze. Strzałki ↑/↓ w zamkniętym polu przestawiają o 15 minut.
// Wartość jak w natywnym polu: „HH:MM" albo pusty string.
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

const props = defineProps({
    modelValue: { type: String, default: '' },
    id: { type: String, default: null },
    placeholder: { type: String, default: null },   // brak = „np. 9:30"
    invalid: { type: Boolean, default: false },
    // Krok listy i strzałek w minutach.
    step: { type: Number, default: 15 },
    // Klasy samego pola (np. węższe w listach drzemek).
    inputClass: { type: [String, Array, Object], default: '' },
    disabled: { type: Boolean, default: false },
    ariaLabel: { type: String, default: null },
});
const emit = defineEmits(['update:modelValue']);

const root = ref(null);
const input = ref(null);
const listEl = ref(null);
const text = ref(props.modelValue ?? '');
const open = ref(false);
const cursor = ref(-1);
// Pisanie zawęża listę; samo otwarcie (focus) pokazuje całą.
const typing = ref(false);

watch(() => props.modelValue, (v) => { if (!open.value || !typing.value) text.value = v ?? ''; });

const pad = (n) => String(n).padStart(2, '0');
const fmt = (mins) => `${pad(Math.floor(mins / 60) % 24)}:${pad(mins % 60)}`;
const toMins = (hhmm) => {
    const [h, m] = (hhmm ?? '').split(':').map(Number);
    return Number.isFinite(h) && Number.isFinite(m) ? h * 60 + m : null;
};

// „9" → 09:00, „930" → 09:30, „0930" → 09:30, „9.3" → 09:30.
// Dwie cyfry po separatorze to minuty; jedna po separatorze — dziesiątki
// („9.3" = 9:30, jak się to mówi), bez separatora ostatnie dwie cyfry to minuty.
const parse = (raw) => {
    const s = (raw ?? '').trim().toLowerCase().replace(/\s*(h|godz\.?)$/, '');
    if (!s) return '';
    let h;
    let m;
    const sep = s.match(/^(\d{1,2})\s*[:.,\s]\s*(\d{1,2})$/);
    if (sep) {
        h = Number(sep[1]);
        m = sep[2].length === 1 ? Number(sep[2]) * 10 : Number(sep[2]);
    } else if (/^\d{1,4}$/.test(s)) {
        if (s.length <= 2) { h = Number(s); m = 0; } else { h = Number(s.slice(0, -2)); m = Number(s.slice(-2)); }
    } else {
        return null;
    }
    if (h > 23 || m > 59) return null;
    return `${pad(h)}:${pad(m)}`;
};

const parsed = computed(() => parse(text.value));
const bad = computed(() => text.value.trim() !== '' && parsed.value === null);

const nowMins = () => { const d = new Date(); return d.getHours() * 60 + d.getMinutes(); };

// Lista: cała doba co `step`, przewinięta do okolic obecnej wartości (albo teraz).
const slots = computed(() => Array.from({ length: Math.round(1440 / props.step) }, (_, i) => fmt(i * props.step)));

const options = computed(() => {
    const now = fmt(nowMins());
    const base = [{ value: now, label: t('common.time.now', { time: now }), now: true }, ...slots.value.map(v => ({ value: v, label: v }))];
    if (!typing.value || !text.value.trim()) return base;
    // Podczas pisania: najpierw dokładnie to, co wpisano, potem pasujące godziny.
    const exact = parsed.value;
    const digits = text.value.replace(/\D/g, '');
    // „9" pasuje do 09:00–09:45, „93" do 09:30, „21" do 21:xx (z zerem z przodu albo bez).
    const matching = slots.value
        .filter((v) => {
            const [h, m] = v.split(':');
            return `${h}${m}`.startsWith(digits) || `${Number(h)}${m}`.startsWith(digits);
        })
        .slice(0, 8)
        .map(v => ({ value: v, label: v }));
    const list = exact && !matching.some(o => o.value === exact) ? [{ value: exact, label: exact }, ...matching] : matching;
    return list;
});

const anchorIndex = () => {
    const target = toMins(parsed.value || props.modelValue) ?? nowMins();
    const idx = options.value.findIndex(o => !o.now && toMins(o.value) >= target);
    return idx === -1 ? options.value.length - 1 : idx;
};

const scrollToCursor = async () => {
    await nextTick();
    const el = listEl.value?.querySelector(`[data-i="${cursor.value}"]`);
    el?.scrollIntoView({ block: 'nearest' });
};

const openList = async () => {
    open.value = true;
    typing.value = false;
    cursor.value = anchorIndex();
    await nextTick();
    // Zaznaczona godzina na środku listy, a nie na dole.
    const el = listEl.value?.querySelector(`[data-i="${cursor.value}"]`);
    if (el && listEl.value) listEl.value.scrollTop = el.offsetTop - listEl.value.clientHeight / 2 + el.clientHeight / 2;
};

const commit = (value) => {
    text.value = value;
    emit('update:modelValue', value);
};

const choose = (o) => {
    commit(o.value);
    open.value = false;
    typing.value = false;
};

const onInput = (e) => {
    text.value = e.target.value;
    typing.value = true;
    open.value = true;
    cursor.value = options.value.length ? 0 : -1;
};

// Na wyjściu z pola: poprawną godzinę normalizujemy, błędną zostawiamy z
// czerwoną ramką (żeby było widać, co poprawić), pustą czyścimy.
const onBlur = () => {
    setTimeout(() => {
        if (root.value?.contains(document.activeElement)) return;
        open.value = false;
        typing.value = false;
        if (parsed.value !== null) commit(parsed.value);
    }, 120);
};

const shift = (dir) => {
    const cur = toMins(parsed.value || props.modelValue);
    const base = cur ?? nowMins();
    // Do najbliższej wielokrotności kroku, potem o krok.
    const snapped = dir > 0 ? Math.floor(base / props.step) * props.step + props.step : Math.ceil(base / props.step) * props.step - props.step;
    commit(fmt((snapped + 1440) % 1440));
};

const onKeydown = (e) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        if (!open.value) { shift(e.key === 'ArrowUp' ? 1 : -1); return; }
        const n = options.value.length;
        if (!n) return;
        cursor.value = (cursor.value + (e.key === 'ArrowDown' ? 1 : -1) + n) % n;
        scrollToCursor();
    } else if (e.key === 'Enter') {
        if (open.value && options.value[cursor.value]) {
            e.preventDefault();
            choose(options.value[cursor.value]);
        } else if (parsed.value !== null) {
            commit(parsed.value);
        }
    } else if (e.key === 'Escape' && open.value) {
        // Escape zamyka listę, a nie cały modal.
        e.preventDefault();
        e.stopPropagation();
        open.value = false;
    } else if (e.key === 'Tab') {
        open.value = false;
    }
};

const onDocDown = (e) => { if (!root.value?.contains(e.target)) open.value = false; };
onMounted(() => document.addEventListener('mousedown', onDocDown));
onUnmounted(() => document.removeEventListener('mousedown', onDocDown));

const listId = `v2-time-list-${Math.random().toString(36).slice(2, 8)}`;
</script>

<template>
    <div ref="root" class="relative">
        <input
            :id="id"
            ref="input"
            :value="text"
            type="text"
            inputmode="numeric"
            autocomplete="off"
            role="combobox"
            :aria-expanded="open"
            :aria-controls="listId"
            :aria-activedescendant="open && cursor >= 0 ? `${listId}-${cursor}` : undefined"
            :aria-invalid="invalid || bad"
            :aria-label="ariaLabel"
            :disabled="disabled"
            class="v2-input v2-input--num !pr-8"
            :class="inputClass"
            :placeholder="placeholder ?? t('common.record.timePlaceholder')"
            @focus="!disabled && openList()"
            @click="!open && !disabled && openList()"
            @input="onInput"
            @keydown="onKeydown"
            @blur="onBlur"
        />
        <i class="pi pi-clock absolute right-3 top-1/2 -translate-y-1/2 text-[11px] text-[#A29A8E] pointer-events-none" aria-hidden="true"></i>

        <ul
            v-if="open && options.length"
            :id="listId"
            ref="listEl"
            role="listbox"
            class="absolute z-30 left-0 right-0 mt-1 max-h-[216px] overflow-y-auto py-1 bg-white border border-[#E4DDD1] rounded-xl shadow-lg"
        >
            <li
                v-for="(o, i) in options"
                :id="`${listId}-${i}`"
                :key="`${o.value}-${o.now ? 'now' : i}`"
                role="option"
                :data-i="i"
                :aria-selected="o.value === modelValue"
                class="px-3 py-1.5 text-[13px] tabular-nums cursor-pointer flex items-center justify-between gap-2"
                :class="[
                    i === cursor ? 'bg-[#F2ECE1] text-[#211E1A]' : 'text-[#4A443C] hover:bg-[#FAF7F2]',
                    o.now ? 'font-semibold text-[#B5643C] border-b border-[#F1ECE3] mb-1' : '',
                ]"
                @mousedown.prevent="choose(o)"
                @mouseenter="cursor = i"
            >
                {{ o.label }}
                <i v-if="o.value === modelValue && !o.now" class="pi pi-check text-[10px] text-[#5B8C6E]" aria-hidden="true"></i>
            </li>
        </ul>

        <p v-if="bad" class="absolute left-0 top-full mt-0.5 text-[10.5px] text-[#B5643C] whitespace-nowrap">{{ t('common.time.formatHint') }}</p>
    </div>
</template>
